<template>
  <div id="data-list-list-view" class="datatable-container">
    <!-- Filters -->
    <vx-card>
      <div class="vx-row">
        <div class="vx-col md:w-1/4 w-full mb-3">
          <label class="text-sm">{{ $t('dashboard.status') }}</label>
          <vs-select v-model="filters.status" class="w-full" @input="onFilterChange">
            <vs-select-item :value="''" text="All Statuses" />
            <vs-select-item
              v-for="opt in source.statusOptions"
              :key="opt.value"
              :value="opt.value"
              :text="opt.text"
            />
          </vs-select>
        </div>
        <div class="vx-col md:w-1/2 w-full mb-3">
          <label class="text-sm">Search (document # / supplier)</label>
          <vs-input
            class="w-full"
            icon-pack="feather"
            icon="icon-search"
            v-model="filters.search"
            @keyup.enter="onFilterChange"
          />
        </div>
      </div>
    </vx-card>

    <!-- Results Table -->
    <vx-card class="mt-5">
      <div class="flex justify-between items-center mb-2">
        <h4 class="card-title">{{ $t('dashboard.results') }} ({{ totalItem }})</h4>
        <vs-button color="primary" type="border" size="small" :disabled="loading" @click="getRecords">
          {{ $t('dashboard.refresh') }}
        </vs-button>
      </div>

      <vs-table
        v-show="loaded"
        stripe
        sst
        @sort="handleSort"
        ref="table"
        :data="rows"
        :noDataText="$t('dashboard.noDataAvailable')"
      >
        <template slot="thead">
          <vs-th sort-key="docNo">{{ $t('dashboard.docNo') }}</vs-th>
          <vs-th sort-key="supplier">Supplier</vs-th>
          <vs-th sort-key="issueDate">{{ $t('dashboard.date') }}</vs-th>
          <vs-th sort-key="total">{{ $t('dashboard.total') }}</vs-th>
          <vs-th>{{ $t('dashboard.status') }}</vs-th>
          <vs-th>{{ $t('dashboard.action') }}</vs-th>
        </template>

        <template slot-scope="{ data }">
          <tbody>
            <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
              <vs-td>
                <span class="font-medium cursor-pointer text-primary" @click="handleView(tr)">
                  {{ tr.docNo || '—' }}
                </span>
              </vs-td>
              <vs-td>
                <p class="text-sm">{{ tr.supplierName || '—' }}</p>
                <p v-if="tr.supplierTaxId" class="text-xs text-gray-500">{{ tr.supplierTaxId }}</p>
              </vs-td>
              <vs-td>
                <span v-if="tr.issueDate" class="text-sm">{{ tr.issueDate | moment('DD MMM YYYY') }}</span>
                <span v-else class="text-gray-400">—</span>
              </vs-td>
              <vs-td>
                <span class="text-sm">{{ formatAmount(tr.total, tr.currency) }}</span>
              </vs-td>
              <vs-td>
                <vs-chip :color="tr.status.color">{{ tr.status.label }}</vs-chip>
              </vs-td>
              <vs-td>
                <feather-icon
                  icon="EyeIcon"
                  svgClasses="w-5 h-5 hover:text-primary stroke-current"
                  class="cursor-pointer"
                  @click.stop="handleView(tr)"
                />
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>

      <div v-if="loading" class="flex justify-center items-center py-10">
        <vs-spinner color="primary" />
      </div>

      <template v-if="totalPage > 1">
        <div class="flex justify-end mt-4">
          <vs-pagination :total="totalPage" v-model="currentPage"></vs-pagination>
        </div>
      </template>
    </vx-card>
  </div>
</template>

<script>
import serverSort from '@/mixins/server-sort'
import { SOURCES, TYPE_BY_ROUTE, jurisdictionOf } from './expenseDocSources'

// Purchases > Expense Doc — the buyer-side mirror of Sales > Billing Doc: one
// plain document list per type, without channel (LHDN / Peppol) detail.

export default {
  mixins: [serverSort],
  data() {
    return {
      itemsPerPage: 25,
      loaded: false,
      loading: false,
      currentPage: 1,
      totalItem: 0,
      totalPage: 0,
      sortBy: { issueDate: 'DESC' },
      defaultSortBy: { issueDate: 'DESC' },
      filters: { status: '', search: '' },
    }
  },
  computed: {
    jurisdiction() {
      return jurisdictionOf(this.$store)
    },
    source() {
      return SOURCES[this.jurisdiction]
    },
    documentType() {
      return (TYPE_BY_ROUTE[this.$route.params.type] || {})[this.jurisdiction]
    },
    rows() {
      return (this.$store.getters[this.source.getter] || []).map(this.source.toRow)
    },
  },
  watch: {
    currentPage(newValue, oldValue) {
      if (newValue !== oldValue) this.getRecords()
    },
    '$route.params.type'() {
      this.filters = { status: '', search: '' }
      this.currentPage = 1
      this.getRecords()
    },
  },
  created() {
    this.getRecords()
  },
  methods: {
    async getRecords() {
      // Type not offered in this jurisdiction (e.g. SG debit notes) — nothing to show.
      if (!this.documentType) {
        this.loaded = true
        this.totalItem = 0
        this.totalPage = 0
        return
      }
      this.loading = true
      try {
        const [sortKey, sortOrder] = Object.entries(this.sortBy)[0] || ['issueDate', 'DESC']
        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage,
          sortBy: this.source.sortFields[sortKey] || this.source.sortFields.issueDate,
          sortOrder,
          [this.source.typeParam]: this.documentType,
        }
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.search) params.search = this.filters.search

        await this.$store.dispatch(this.source.listAction, params)
        const pagination = this.$store.state[this.source.state].pagination || {}
        this.currentPage = pagination.currentPage || 1
        this.totalItem = pagination.totalItem || this.rows.length
        this.totalPage = pagination.totalPage || 1
        this.loaded = true
      } catch (e) {
        console.error('[ExpenseDocs] fetch failed:', e)
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: e.response?.data?.message || 'Failed to fetch expense documents',
        })
      } finally {
        this.loading = false
      }
    },

    onFilterChange() {
      this.currentPage = 1
      this.getRecords()
    },

    handleView(row) {
      if (row.id) this.$router.push(`/expense-docs/${row.id}`).catch(() => {})
    },

    formatAmount(amount, currency) {
      if (amount == null || isNaN(amount)) return '—'
      const value = Number(amount).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return currency ? `${currency} ${value}` : value
    },
  },
}
</script>

<style lang="scss" scoped>
.datatable-container {
  .vs-table--tbody-table {
    .vs-table--td {
      padding: 12px 8px;
    }
  }
}
</style>
